import { DocumentViewerMessage } from '@st/document-viewer-kit'
import { clsx } from 'clsx'
import {
  AnnotationLocalMessage,
  selAnnotations,
  selAnnotationsPluginState,
  toAnnotationsPluginMessage
} from '@st/document-viewer-kit/plugins/annotations'
import { Button, Dialog, DialogButtons, LinkIcon, Modal, RotateCWIcon, Tip } from '@st/theme'
import type { JSON, STDocument, TaxReturnUnit } from '@st/sdk'
import { denormalizeRect, type Rect } from '@st/geom'
import { deepEqual } from '@st/util/deep-equal'
import { useMemo, useState } from 'react'
import {
  buildSchemaSections,
  formatDataValue,
  valueAtPath,
  type JSONSchema,
  type SchemaRow,
  type SchemaSection
} from '~/st-doc-viewer/schema-document-data'
import type { ViewerFieldAnnotation } from '~/st-doc-viewer/viewer-annotations'
import {
  ViewerSidebarContent,
  ViewerSidebarBody,
  ViewerSidebarRow,
  ViewerSidebarSection,
  ViewerSidebarToolbar,
  ViewerSidebarValue
} from '~/st-doc-viewer/sidebar-layout'
import { useSelect } from '~/store'

export function FieldsSidebar({
  sendViewerMessage,
  pages,
  onRefreshTaxReturn,
  documentTypeId,
  taxReturnUnitPath,
  documentData,
  dataSchema,
  onLinkTaxReturnUnit,
  taxReturnUnits
}: {
  sendViewerMessage: (message: DocumentViewerMessage) => void
  pages: { pageNumber: number; bounds: Rect }[]
  onRefreshTaxReturn?: () => void
  documentTypeId?: string
  taxReturnUnitPath?: TaxReturnUnit['path']
  documentData: STDocument['data']
  dataSchema: JSONSchema
  onLinkTaxReturnUnit: (taxReturnUnitPath: TaxReturnUnit['path']) => void
  taxReturnUnits: TaxReturnUnit[]
}) {
  const [linkDialog, setLinkDialog] = useState<
    { selectedTaxReturnUnit?: TaxReturnUnit } | undefined
  >()
  const sendAnnotationMessage = (message: AnnotationLocalMessage) =>
    sendViewerMessage(toAnnotationsPluginMessage(message))

  const fieldAnnotations = useSelect('stFolder', (state) =>
    Object.values(
      selAnnotations(
        state.viewer,
        (annotation): annotation is ViewerFieldAnnotation => annotation.type == 'field'
      )
    )
  )
  const firstAnnotationByPath = useMemo(() => {
    const annotationsByPath = new Map<string, ViewerFieldAnnotation>()

    for (const annotation of fieldAnnotations) {
      if (annotation.mode != 'data' || !annotation.key) continue
      if (!annotationsByPath.has(annotation.key)) {
        annotationsByPath.set(annotation.key, annotation)
      }
    }

    return annotationsByPath
  }, [fieldAnnotations])
  const selectionState = useSelect(
    'stFolder',
    (state) => selAnnotationsPluginState(state.viewer).selectionState
  )
  const candidateTaxReturnUnits = taxReturnUnits.filter(
    (unit) => unit.documentTypeId == documentTypeId
  )
  const linkedTaxReturnUnit = taxReturnUnits.find(
    (unit) => deepEqual(unit.path, taxReturnUnitPath) && unit.documentTypeId == documentTypeId
  )
  const sections = buildSchemaSections({
    schema: dataSchema,
    documentData,
    taxReturnData: linkedTaxReturnUnit?.data
  })

  return (
    <ViewerSidebarContent>
      <ViewerSidebarToolbar ariaLabel="Tax return actions">
        {onRefreshTaxReturn ? (
          <Tip title="Refresh tax return">
            <Button
              variant="icon"
              aria-label="Refresh tax return"
              leadingIcon={<RotateCWIcon className="size-4" />}
              onClick={onRefreshTaxReturn}
            />
          </Tip>
        ) : null}
        <Tip title="Link to tax return">
          <Button
            variant="icon"
            aria-label="Link to tax return"
            leadingIcon={<LinkIcon className="size-4" />}
            onClick={() =>
              setLinkDialog({
                selectedTaxReturnUnit: linkedTaxReturnUnit ?? candidateTaxReturnUnits[0]
              })
            }
          />
        </Tip>
      </ViewerSidebarToolbar>

      <ViewerSidebarBody>
        {sections.map((section) => (
          <SchemaSectionRows
            key={section.path}
            section={section}
            linkedTaxReturnData={linkedTaxReturnUnit?.data}
            documentData={documentData}
            selectionState={selectionState}
            annotationForPath={(path) => firstAnnotationByPath.get(path)}
            onSelectAnnotation={(annotation) => {
              const page = pages.find((candidate) => candidate.pageNumber == annotation.pageNumber)

              sendAnnotationMessage({ type: 'selectAnnotation', annotationId: annotation.id })
              if (page) {
                sendViewerMessage({
                  type: 'requestViewerScroll',
                  target: {
                    pageNumber: annotation.pageNumber,
                    bounds: denormalizeRect(annotation.bounds, page.bounds)
                  },
                  behavior: 'smooth'
                })
              }
            }}
          />
        ))}
      </ViewerSidebarBody>

      {linkDialog ? (
        <LinkTaxReturnDialog
          taxReturnUnits={taxReturnUnits}
          candidateTaxReturnUnits={candidateTaxReturnUnits}
          selectedTaxReturnUnit={linkDialog.selectedTaxReturnUnit}
          onSelectedTaxReturnUnitChange={(selectedTaxReturnUnit) =>
            setLinkDialog({ selectedTaxReturnUnit })
          }
          onClose={() => setLinkDialog(undefined)}
          onLink={() => {
            if (!linkDialog.selectedTaxReturnUnit) {
              return
            }

            onLinkTaxReturnUnit(linkDialog.selectedTaxReturnUnit.path)
            setLinkDialog(undefined)
          }}
        />
      ) : null}
    </ViewerSidebarContent>
  )
}

function SchemaSectionRows({
  section,
  documentData,
  linkedTaxReturnData,
  selectionState,
  annotationForPath,
  onSelectAnnotation
}: {
  section: SchemaSection
  documentData: JSON
  linkedTaxReturnData?: JSON
  selectionState: ReturnType<typeof selAnnotationsPluginState>['selectionState']
  annotationForPath: (path: string) => ViewerFieldAnnotation | undefined
  onSelectAnnotation: (annotation: ViewerFieldAnnotation) => void
}) {
  return (
    <ViewerSidebarSection label={section.label}>
      {section.rows.map((row) => {
        const annotation = annotationForPath(row.path)
        const selected =
          annotation != null &&
          selectionState.type != 'none' &&
          selectionState.annotationId == annotation.id

        return (
          <SchemaRowView
            key={row.path}
            row={row}
            selected={selected}
            documentValue={valueAtPath(documentData, row.path)}
            taxReturnValue={
              linkedTaxReturnData ? valueAtPath(linkedTaxReturnData, row.path) : undefined
            }
            showTaxReturnValue={linkedTaxReturnData != null}
            onClick={annotation ? () => onSelectAnnotation(annotation) : undefined}
          />
        )
      })}
    </ViewerSidebarSection>
  )
}

function SchemaRowView({
  row,
  selected,
  documentValue,
  taxReturnValue,
  showTaxReturnValue,
  onClick
}: {
  row: SchemaRow
  selected: boolean
  documentValue: JSON
  taxReturnValue: JSON
  showTaxReturnValue: boolean
  onClick?: () => void
}) {
  const content = (
    <ViewerSidebarRow label={row.label} depth={row.depth}>
      <FieldValue label="Document" value={documentValue} />
      {showTaxReturnValue ? <FieldValue label="Tax return" value={taxReturnValue} /> : null}
    </ViewerSidebarRow>
  )

  if (!onClick) {
    return content
  }

  return (
    <button
      type="button"
      className={selected ? 'block w-full bg-stone-100 text-left' : 'block w-full text-left'}
      onClick={onClick}
    >
      {content}
    </button>
  )
}

function FieldValue({ label, value }: { label: string; value: JSON }) {
  return <ViewerSidebarValue label={label}>{formatDataValue(value)}</ViewerSidebarValue>
}

function LinkTaxReturnDialog({
  taxReturnUnits,
  candidateTaxReturnUnits,
  selectedTaxReturnUnit,
  onSelectedTaxReturnUnitChange,
  onClose,
  onLink
}: {
  taxReturnUnits: TaxReturnUnit[]
  candidateTaxReturnUnits: TaxReturnUnit[]
  selectedTaxReturnUnit?: TaxReturnUnit
  onSelectedTaxReturnUnitChange: (taxReturnUnit: TaxReturnUnit | undefined) => void
  onClose: () => void
  onLink: () => void
}) {
  const canLink = candidateTaxReturnUnits.length > 0 && selectedTaxReturnUnit != null

  return (
    <Modal isOpen={true} onClose={onClose}>
      <Dialog
        title="Link to tax return"
        subtitle="Select the pulled tax return unit for this document."
        buttons={
          <DialogButtons>
            <Button variant="default" onClick={onClose}>
              Close
            </Button>
            {candidateTaxReturnUnits.length > 0 ? (
              <Button variant="primary" disabled={!canLink} onClick={onLink}>
                Link
              </Button>
            ) : null}
          </DialogButtons>
        }
      >
        {candidateTaxReturnUnits.length > 0 ? (
          <select
            aria-label="Tax return unit"
            className="w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm text-stone-950"
            value={formatUnitPath(selectedTaxReturnUnit?.path ?? [])}
            onChange={(event) =>
              onSelectedTaxReturnUnitChange(
                candidateTaxReturnUnits.find(
                  (unit) => formatUnitPath(unit.path) == event.target.value
                )
              )
            }
          >
            {candidateTaxReturnUnits.map((unit) => (
              <option key={formatUnitPath(unit.path)} value={formatUnitPath(unit.path)}>
                {formatUnitTitle(unit)}
              </option>
            ))}
          </select>
        ) : (
          <p className="text-sm text-stone-700">
            {taxReturnUnits.length == 0
              ? 'Pull a tax return before linking this document.'
              : 'No pulled tax return units are available for this document type.'}
          </p>
        )}
      </Dialog>
    </Modal>
  )
}

function formatUnitTitle(unit: TaxReturnUnit) {
  if (unit.label) {
    return unit.label
  }

  return formatUnitPath(unit.path)
}

function formatUnitPath(path: TaxReturnUnit['path']) {
  return path.map((segment) => String(segment)).join(' / ')
}
